import { isEmpty } from 'lodash';

import type { NetworkLevel } from 'hb-builder/types';

export type ItemTypeSelector =
  | 'news'
  | 'events'
  | 'posts'
  | 'topics'
  | 'discussions'
  | 'empty';

export type DataTypeSelector = 'manually' | 'last_published' | 'upcoming';

export interface Option {
  label: string;
  value: number | string;
}

export type Categories = Record<
  Partial<NetworkLevel>,
  Record<Partial<ItemTypeSelector>, Record<'categories', Option[]>>
>;

interface QueryOptions {
  categories?: { label: string; value: string }[];
  dataType: DataTypeSelector;
  numberOfItems?: number;
}

interface DataTypeSelectorOptionsProps {
  includeFollowedByUserGroups?: boolean;
  includeHighlightedEvents?: boolean;
  includeManualSearch?: boolean;
  includePastEvents?: boolean;
  includeLatestPosts?: boolean;
  includeTopPosts?: boolean;
  networkLevel?: NetworkLevel;
  type?: ItemTypeSelector;
}

interface DataTypeSelectorCategoriesProps {
  categories: Partial<Categories>;
  type?: ItemTypeSelector;
  networkLevel?: NetworkLevel;
}

interface ContentTypesOptionsProps {
  showPosts?: boolean;
  showUserLane?: boolean;
  showTopics?: boolean;
  showDiscussions?: boolean;
}

interface SanitizeQueryBuildProps {
  contentType: ItemTypeSelector;
  id: string;
  onChange: (arg: { id: string; settingKey: string; value: string }) => void;
  queryOptions?: QueryOptions;
}

const DATA_TYPES_WITH_SELECTOR_OPTIONS = [
  'discussions',
  'news',
  'events',
  'posts',
  'topics',
];

const DATA_TYPES_WITH_SELECTOR_CATEGORIES = [
  'discussions',
  'news',
  'events',
  'posts',
  'topics',
];

export const dataTypeSelectorOptions = ({
  includeFollowedByUserGroups,
  includeHighlightedEvents,
  includeManualSearch,
  includePastEvents,
  includeLatestPosts,
  includeTopPosts,
  networkLevel = 'network',
  type,
}: DataTypeSelectorOptionsProps): Option[] => {
  if (!type || !DATA_TYPES_WITH_SELECTOR_OPTIONS.includes(type)) {
    return [];
  }

  return (
    getDataTypeOptions({
      includeFollowedByUserGroups,
      includeHighlightedEvents,
      includeManualSearch,
      includePastEvents,
      includeLatestPosts,
      includeTopPosts,
    })[type][networkLevel] || []
  );
};

export const dataTypeSelectorCategories = ({
  categories,
  networkLevel = 'network',
  type,
}: DataTypeSelectorCategoriesProps): Option[] => {
  if (
    isEmpty(categories) ||
    !type ||
    DATA_TYPES_WITH_SELECTOR_CATEGORIES.indexOf(type) === -1
  ) {
    return [];
  }

  return categories[networkLevel]?.[type]?.categories || [];
};

export const getContentTypesOptions = ({
  showPosts,
  showUserLane,
  showTopics,
  showDiscussions,
}: ContentTypesOptionsProps = {}): Option[] =>
  [
    { value: 'empty', label: s__('Builder|Select a type') },
    { value: 'events', label: __('Events') },
    { value: 'news', label: __('News') },
    showPosts && { value: 'posts', label: s__('Builder|Live feed posts') },
    showUserLane && { value: 'users', label: __('Users') },
    showTopics && { value: 'topics', label: __('Groups') },
    showDiscussions && { value: 'discussions', label: __('Forum discussions') },
  ].filter(Boolean) as Option[];

export const sanitizeQueryBuild = ({
  onChange,
  id,
  queryOptions,
  contentType,
}: SanitizeQueryBuildProps): void => {
  // handle impossible Query
  if (queryOptions?.dataType === 'upcoming' && contentType === 'news') {
    onChange({
      id,
      settingKey: 'attributes.dynamicAttributes.query.dataType',
      value: 'last_published',
    });
  }
};

export const CONTENT_TYPES_USER_LANE_OPTIONS = [
  { value: 'recently_joined', label: __('Recently joined (last month)') },
  { value: 'close', label: __('Around me (150km radius)') },
];

export const USER_LANE_SUBLINE_OPTIONS = [
  { value: 'job', label: __('Current position') },
  { value: 'location', label: __('Location') },
  { value: 'cluster', label: __('Cluster') },
];

const getDataTypeOptions = ({
  includeFollowedByUserGroups,
  includeHighlightedEvents,
  includeManualSearch,
  includePastEvents,
  includeLatestPosts,
  includeTopPosts,
}) =>
  Object.freeze({
    discussions: {
      topic: [
        {
          value: 'last_published',
          label: s__('Builder|Last discussions'),
        },
        {
          value: 'pinned',
          label: s__('Builder|Highlighted discussions'),
        },
        {
          value: 'most_popular',
          label: s__('Builder|Most popular'),
        },
        {
          value: 'followed_by_user',
          label: s__('Builder|Followed by the user'),
        },
      ],
      network: [
        {
          value: 'last_published',
          label: s__('Builder|Last discussions'),
        },
        {
          value: 'pinned',
          label: s__('Builder|Highlighted discussions'),
        },
        {
          value: 'most_popular',
          label: s__('Builder|Most popular'),
        },
        {
          value: 'followed_by_user',
          label: s__('Builder|Followed by the user'),
        },
      ],
    },
    news: {
      topic: [
        ...(includeManualSearch
          ? [
              {
                value: 'manually',
                label: s__('Builder|Manually'),
              },
            ]
          : []),
        {
          value: 'last_published',
          label: s__('Builder|Last published group news'),
        },
      ],
      network: [
        ...(includeManualSearch
          ? [
              {
                value: 'manually',
                label: s__('Builder|Manually'),
              },
            ]
          : []),
        {
          value: 'last_published',
          label: s__('Builder|Last published global news'),
        },
      ],
    },
    events: {
      topic: [
        ...(includeManualSearch
          ? [
              {
                value: 'manually',
                label: s__('Builder|Manually'),
              },
            ]
          : []),
        {
          value: 'last_published',
          label: s__('Builder|Last published group events'),
        },
        {
          value: 'upcoming',
          label: s__('Builder|Upcoming group events'),
        },
        ...(includePastEvents
          ? [
              {
                value: 'past',
                label: s__('Builder|Past group events'),
              },
            ]
          : []),
      ],
      network: [
        ...(includeHighlightedEvents
          ? [
              {
                value: 'highlighted',
                label: s__('Builder|Highlighted events'),
              },
            ]
          : []),
        ...(includeManualSearch
          ? [
              {
                value: 'manually',
                label: s__('Builder|Manually'),
              },
            ]
          : []),
        {
          value: 'last_published',
          label: s__('Builder|Last published global events'),
        },
        {
          value: 'upcoming',
          label: s__('Builder|Upcoming global events'),
        },
        ...(includePastEvents
          ? [
              {
                value: 'past',
                label: s__('Builder|Past global events'),
              },
            ]
          : []),
      ],
    },
    posts: {
      topic: [
        ...(includeLatestPosts
          ? [
              {
                value: 'last_created',
                label: s__('Builder|Latest live feed posts '),
              },
            ]
          : []),
      ],
      network: [
        ...(includeTopPosts
          ? [
              {
                value: 'top_post',
                label: s__('Builder|Top live feed posts '),
              },
            ]
          : []),
        ...(includeLatestPosts
          ? [
              {
                value: 'last_created',
                label: s__('Builder|Latest live feed posts '),
              },
            ]
          : []),
      ],
    },
    topics: {
      topic: [
        {
          value: 'last_created',
          label: s__('Builder|Last created groups'),
        },
        {
          value: 'suggested_for_user',
          label: s__('Builder|Groups suggested for users'),
        },
        ...(includeFollowedByUserGroups
          ? [
              {
                value: 'followed_by_user',
                label: s__('Builder|Groups the user is a member of'),
              },
            ]
          : []),
      ],
      network: [
        {
          value: 'last_created',
          label: s__('Builder|Last created groups'),
        },
        {
          value: 'suggested_for_user',
          label: s__('Builder|Groups suggested for users'),
        },
        ...(includeFollowedByUserGroups
          ? [
              {
                value: 'followed_by_user',
                label: s__('Builder|Groups the user is a member of'),
              },
            ]
          : []),
      ],
    },
  });
